import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useConfig } from '@/contexts/ConfigContext';
import { useInstanceId } from '@/contexts/InstanceIdContext';
import { VerifyTerms } from '@/components/verifyTerms/VerifyTerms';
import { useClientColorModeSync } from '@/hooks/theme/useClientColorModeSync';
import { useClientRouteHandler } from '@/hooks/routing/useClientRouteHandler';
import { useClientAuthenticationHandler } from '@/hooks/authentication/useClientAuthenticationHandler';

interface ClientFrameProps {
  onLoad: () => void;
  onError: () => void;
}

// These params are handled by the parent frame
const QUERY_PARAMS_TO_OMIT = ['error', 'amworkspaceid', 'expiry', 'admin'];

export const ClientFrame: React.FC<ClientFrameProps> = ({
  onLoad,
  onError,
}) => {
  useClientRouteHandler();
  useClientAuthenticationHandler();
  const syncColorMode = useClientColorModeSync();
  const { IFRAME_URL } = useConfig();
  const { instanceId } = useInstanceId();
  const { search } = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  const initialPath = useRef(
    instanceId ? window.location.pathname.split(instanceId)[1] : '/home',
  );

  const initialSearch = useRef(
    (() => {
      const params = new URLSearchParams(search);
      QUERY_PARAMS_TO_OMIT.forEach((param) => {
        params.delete(param);
      });
      return params.toString();
    })(),
  );

  const iframeSrc = useMemo(() => {
    return `${IFRAME_URL}${initialPath.current ? initialPath.current : ''}?instanceId=${instanceId}&authHandledByParent=true&${initialSearch.current}`;
  }, [IFRAME_URL, instanceId, initialPath, initialSearch]);

  const handleIframeLoad = useCallback(() => {
    syncColorMode();
    onLoad();
    setIsLoading(false);
  }, [onLoad, syncColorMode]);

  return (
    <VerifyTerms>
      <iframe
        className="flex h-full w-full items-center justify-center"
        id="dasl-iframe"
        data-loading={isLoading}
        data-testid="dasl-iframe"
        src={iframeSrc}
        title="Antimatter"
        onLoad={handleIframeLoad}
        onError={onError}
      />
    </VerifyTerms>
  );
};

export default ClientFrame;
